/**
 * A v4 UUID, in every context the UI actually runs in.
 *
 * ## Why not `crypto.randomUUID()` directly
 *
 * It exists only in a **secure context** — HTTPS, or localhost. On any other
 * origin it is simply `undefined`, and the call throws
 * `crypto.randomUUID is not a function`.
 *
 * That is not a hypothetical. Every mutation in `api/hooks.ts` sends an
 * idempotency key, so on a plain-HTTP origin **every POST fails** — which is
 * exactly what happened the first time the UI was opened from a phone over the
 * LAN, on `http://192.168.68.59:5173`. Uploading a photographed receipt failed
 * before it began, and so would posting an entry or issuing an invoice.
 *
 * Production is HTTPS and would not have hit it. Anything else — LAN testing, a
 * plain-HTTP staging box, an IP address — does.
 *
 * `crypto.getRandomValues` carries no such restriction, so the fallback is a
 * real v4 UUID from the same CSPRNG rather than a weaker substitute. An
 * idempotency key that collided would make one financial POST return another's
 * response, so `Math.random()` is not an acceptable source here.
 */
export function uuid(): string {
  // Preferred where available: it is the platform's own implementation.
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Version 4, variant 1 — the two fields RFC 4122 fixes.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex: string[] = [];
  for (const byte of bytes) {
    hex.push(byte.toString(16).padStart(2, '0'));
  }
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-` +
    `${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-` +
    `${hex.slice(10, 16).join('')}`
  );
}
